/**
 * Bedienhilfen fuer Knoepfe, die mehr koennen als einfaches Klicken.
 *
 * - useLongPress: kurz tippen und lange druecken loesen Verschiedenes aus
 *   (Masterprompt Abschnitt 74: Last Wave).
 * - useHoldRepeat: halten loest wiederholt aus (Abschnitt 70: halten = mehrfach
 *   hinzufuegen).
 * - useTap: ein Knopf, der auch dann reagiert, wenn schon ein anderer Finger
 *   auf dem Schirm liegt.
 * - useHold: gilt nur, solange der Finger liegt (Abschnitt 13: Watch
 *   Frontline).
 *
 * Die Zeitregeln selbst stehen in input/gestures.ts und sind dort getestet.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  HOLD_REPEAT_DELAY_MS,
  HOLD_REPEAT_INTERVAL_MS,
  LONG_PRESS_MS,
  TAP_MAX_DISTANCE,
  classifyPress,
  isClickEcho,
} from '../input/gestures.js';

interface PressHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
  /** Verhindert das Kontextmenue, das ein langes Druecken auf dem Handy oeffnet. */
  onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Kurz tippen -> onTap, lange druecken -> onLongPress.
 *
 * Das lange Druecken loest schon WAEHREND des Haltens aus, sobald die Zeit
 * erreicht ist - man muss nicht erst loslassen, um zu merken, dass es geklappt hat.
 */
export function useLongPress(onTap: () => void, onLongPress: () => void): PressHandlers {
  const start = useRef<{ time: number; x: number; y: number; maxDistance: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longFired = useRef(false);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  return {
    onPointerDown: (event) => {
      start.current = {
        time: performance.now(),
        x: event.clientX,
        y: event.clientY,
        maxDistance: 0,
      };
      longFired.current = false;
      timer.current = setTimeout(() => {
        if (start.current && start.current.maxDistance <= TAP_MAX_DISTANCE) {
          longFired.current = true;
          onLongPress();
        }
      }, LONG_PRESS_MS);
    },
    onPointerMove: (event) => {
      if (!start.current) return;
      const distance = Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y);
      start.current.maxDistance = Math.max(start.current.maxDistance, distance);
    },
    onPointerUp: () => {
      const beruehrung = start.current;
      clear();
      if (!beruehrung || longFired.current) return;
      const kind = classifyPress(performance.now() - beruehrung.time, beruehrung.maxDistance);
      if (kind === 'tap') onTap();
    },
    onPointerCancel: clear,
    onPointerLeave: clear,
    onContextMenu: (event) => event.preventDefault(),
  };
}

/**
 * Loest sofort einmal aus und wiederholt, solange der Finger bleibt.
 * Die erste Wiederholung kommt erst nach einer kleinen Pause.
 *
 * WICHTIG - im Spiel aufgefallen (24.09.2026): Das Loslassen wird am FENSTER
 * abgehoert, nicht am Knopf. Sonst laeuft die Wiederholung ewig weiter, sobald
 * der Knopf waehrend des Haltens ausgegraut wird: Ein deaktivierter Knopf
 * bekommt kein pointerup mehr, und React nimmt ihm ausserdem die Handler ab.
 * Genau das passierte beim Zusammenstellen einer Welle, sobald das Gold
 * ausging - die Creeps liefen dann endlos weiter.
 */
export function useHoldRepeat(onFire: () => void): PressHandlers {
  const delay = useRef<ReturnType<typeof setTimeout> | null>(null);
  const repeat = useRef<ReturnType<typeof setInterval> | null>(null);
  // Immer die neueste Funktion benutzen - sonst wuerde eine laufende
  // Wiederholung mit einem veralteten Stand weiterarbeiten.
  const fire = useRef(onFire);
  useEffect(() => {
    fire.current = onFire;
  }, [onFire]);

  // Meldet die Zuhoerer am Fenster wieder ab. Ein AbortController spart es,
  // dieselbe Funktion zum Abmelden aufheben zu muessen.
  const abbruch = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (delay.current) clearTimeout(delay.current);
    if (repeat.current) clearInterval(repeat.current);
    delay.current = null;
    repeat.current = null;

    abbruch.current?.abort();
    abbruch.current = null;
  }, []);

  useEffect(() => stop, [stop]);

  return {
    onPointerDown: () => {
      stop();
      fire.current();

      // Loslassen kann auch ausserhalb des Knopfes passieren - oder der Knopf
      // ist bis dahin gar nicht mehr da.
      const controller = new AbortController();
      abbruch.current = controller;
      window.addEventListener('pointerup', () => stop(), { signal: controller.signal });
      window.addEventListener('pointercancel', () => stop(), { signal: controller.signal });

      delay.current = setTimeout(() => {
        fire.current();
        repeat.current = setInterval(() => fire.current(), HOLD_REPEAT_INTERVAL_MS);
      }, HOLD_REPEAT_DELAY_MS);
    },
    onPointerMove: () => {},
    onPointerUp: stop,
    onPointerCancel: stop,
    onPointerLeave: stop,
    onContextMenu: (event) => event.preventDefault(),
  };
}

/** Handler fuer einen Knopf, der nur wirkt, solange er gehalten wird. */
export interface HoldHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onLostPointerCapture: () => void;
  onContextMenu: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * Ein Knopf, der nur wirkt, solange er gehalten wird.
 *
 * Gedacht fuer Watch Frontline (Abschnitt 13): hinsehen, solange der
 * Daumen liegt, und beim Loslassen sofort zurueck.
 *
 * Zwei Dinge sind hier wichtiger als bei einem normalen Knopf:
 *
 *   - Der Finger wird **eingefangen** (setPointerCapture). Rutscht er beim
 *     Halten vom Knopf herunter, kommt das Loslassen trotzdem an. Ohne das
 *     bliebe die Kamera an der Front haengen - und der Spieler saehe
 *     seinen Hero nicht mehr.
 *   - Es zaehlt nur EIN Finger. Ein zweiter, der daneben tippt, darf das
 *     Loslassen des ersten nicht vortaeuschen.
 */
export function useHold(onStart: () => void, onEnd: () => void): HoldHandlers {
  const finger = useRef<number | null>(null);

  const beenden = useCallback(
    (pointerId: number | null) => {
      if (finger.current === null) return;
      if (pointerId !== null && pointerId !== finger.current) return;
      finger.current = null;
      onEnd();
    },
    [onEnd],
  );

  // Wird der Knopf mitten im Halten abgeraeumt, bliebe die Sicht sonst haengen.
  useEffect(() => () => beenden(null), [beenden]);

  return {
    onPointerDown: (event) => {
      if (finger.current !== null) return;
      finger.current = event.pointerId;
      /*
       * Das Einfangen darf nicht entscheiden, ob der Knopf wirkt. Manche
       * Browser werfen hier fuer einen Finger, den sie nicht mehr kennen -
       * dann liefe sonst gar nichts mehr.
       */
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // Ohne Einfangen bleibt nur das Loslassen auf dem Knopf selbst.
      }
      onStart();
    },
    onPointerUp: (event) => beenden(event.pointerId),
    onPointerCancel: (event) => beenden(event.pointerId),
    onLostPointerCapture: () => beenden(null),
    onContextMenu: (event) => event.preventDefault(),
  };
}

/** Handler fuer einen Knopf, der auch beim Laufen reagiert. */
export interface TapHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
  onClick: () => void;
}

/**
 * Ein Knopf, der auch dann reagiert, wenn schon ein anderer Finger auf dem
 * Schirm liegt (im Spiel aufgefallen am 27.09.2026).
 *
 * Das Problem: Auf dem Handy erzeugt der Browser nur fuer den ERSTEN Finger
 * ein click-Ereignis. Wer mit dem linken Daumen laeuft und gleichzeitig mit
 * dem rechten ARMY antippt, loeste deshalb gar nichts aus - der Knopf schien
 * kaputt. Die Faehigkeiten hatten das Problem nie, weil sie schon immer auf
 * pointerdown hoeren.
 *
 * Die Loesung: auf pointerup reagieren. Das kommt fuer jeden Finger. Das
 * click danach wird als Nachhall erkannt und uebersprungen - ausser es kommt
 * ohne vorheriges Tippen, dann war es die Tastatur.
 *
 * Es bleibt bewusst pointerUP und nicht pointerDOWN: Wer danebentippt, soll
 * den Finger noch wegziehen koennen. Deshalb zaehlt auch nur, wer sich kaum
 * bewegt hat.
 */
export function useTap(onTap: () => void): TapHandlers {
  const start = useRef<{ x: number; y: number; maxDistance: number } | null>(null);
  /** Wann zuletzt per Finger ausgeloest wurde - fuer die Nachhall-Sperre. */
  const zuletzt = useRef(Number.NEGATIVE_INFINITY);

  return {
    onPointerDown: (event) => {
      start.current = { x: event.clientX, y: event.clientY, maxDistance: 0 };
    },
    onPointerMove: (event) => {
      const beruehrung = start.current;
      if (!beruehrung) return;
      const distance = Math.hypot(event.clientX - beruehrung.x, event.clientY - beruehrung.y);
      beruehrung.maxDistance = Math.max(beruehrung.maxDistance, distance);
    },
    onPointerUp: () => {
      const beruehrung = start.current;
      start.current = null;
      if (!beruehrung || beruehrung.maxDistance > TAP_MAX_DISTANCE) return;
      zuletzt.current = performance.now();
      onTap();
    },
    onPointerCancel: () => {
      start.current = null;
    },
    onPointerLeave: () => {
      start.current = null;
    },
    onClick: () => {
      if (isClickEcho(performance.now() - zuletzt.current)) return;
      onTap();
    },
  };
}
